import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  ShieldCheck,
  Building2,
  CheckCircle2,
  Smartphone,
  CreditCard,
  QrCode,
  Lock,
  ArrowRight,
  ExternalLink,
  Copy,
  Receipt,
  Sparkles,
  Download,
  AlertCircle,
  Clock,
  Send,
  Calendar,
  Check,
  Award
} from 'lucide-react';
import { PaymentAgreement } from './PaymentAgreementTypes';
import { PaymentAgreementPdfService } from '@/services/paymentAgreementPdfService';
import { toast } from 'sonner';

interface DebtorPortalModalProps {
  isOpen: boolean;
  onClose: () => void;
  agreement: PaymentAgreement | null;
  onPaymentSuccess?: (agreementId: string, cuotaNumero: number, refPago: string) => void;
}

export const DebtorPortalModal: React.FC<DebtorPortalModalProps> = ({
  isOpen,
  onClose,
  agreement,
  onPaymentSuccess
}) => {
  if (!agreement) return null;

  const [paymentMethod, setPaymentMethod] = useState<'pse' | 'nequi' | 'card' | 'barras'>('pse');
  const [selectedBank, setSelectedBank] = useState('Bancolombia');
  const [payerEmail, setPayerEmail] = useState(agreement.email || 'propietario@email.com');
  const [otpCode, setOtpCode] = useState('');
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [isSigned, setIsSigned] = useState(agreement.cuota_inicial_pagada || agreement.estado === 'firmado_activo' || agreement.estado === 'al_dia');
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [paymentReceipt, setPaymentReceipt] = useState<{
    referencia: string;
    fecha: string;
    monto: number;
    metodo: string;
    banco: string;
  } | null>(null);

  const handleSendOtp = () => {
    setIsOtpSent(true);
    toast.success(`Código de verificación enviado al móvil ${agreement.telefono || 'del propietario'}.`);
  };

  const handleSignAgreement = () => {
    if (!otpCode && !isSigned) {
      toast.error('Por favor digite el código de seguridad OTP para firmar el acuerdo.');
      return;
    }
    setIsSigned(true);
    toast.success('Acuerdo de Transacción firmado digitalmente con éxito bajo Ley 527 de 1999.');
  };

  const handleSimulatePayment = () => {
    if (!isSigned) {
      toast.error('Debe firmar y aceptar los términos del acuerdo antes de realizar el pago.');
      return;
    }

    setIsProcessingPayment(true);
    setTimeout(() => {
      setIsProcessingPayment(false);
      const ref = `PSE-COL-${Math.floor(100000 + Math.random() * 900000)}`;
      const amount = agreement.cuota_inicial_pagada 
        ? agreement.valor_cuota_mensual 
        : agreement.cuota_inicial_monto;

      setPaymentReceipt({
        referencia: ref,
        fecha: new Date().toLocaleString('es-CO'),
        monto: amount,
        metodo: paymentMethod.toUpperCase(),
        banco: paymentMethod === 'pse' ? selectedBank : 'Billetera Digital'
      });

      if (onPaymentSuccess) {
        const cuotaIndex = agreement.cuota_inicial_pagada ? (agreement.cuotas_pagadas + 1) : 0;
        onPaymentSuccess(agreement.id, cuotaIndex, ref);
      }

      toast.success(`¡Pago por $${amount.toLocaleString('es-CO')} procesado y acreditado con éxito!`);
    }, 1200);
  };

  const ahorro = agreement.intereses_condonados || 0;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl p-0 overflow-hidden border-slate-200 dark:border-slate-800 shadow-2xl">
        {/* Barra superior institucional del Portal del Deudor */}
        <div className="bg-slate-900 text-white p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-800">
          <div className="flex items-center gap-3">
            {agreement.copropiedad_logo_data || agreement.copropiedad_logo_url ? (
              <div className="h-11 w-11 rounded-xl bg-white p-1 flex items-center justify-center shadow-md shrink-0 overflow-hidden">
                <img
                  src={agreement.copropiedad_logo_data || agreement.copropiedad_logo_url}
                  alt={agreement.copropiedad_nombre}
                  className="max-h-full max-w-full object-contain"
                />
              </div>
            ) : (
              <div className="h-10 w-10 rounded-xl bg-gradient-to-tr from-emerald-500 to-teal-400 flex items-center justify-center text-white shadow-md">
                <Building2 className="h-5 w-5" />
              </div>
            )}
            <div>
              <div className="flex items-center gap-2">
                <span className="font-bold text-sm tracking-wide uppercase">
                  {agreement.copropiedad_nombre}
                </span>
                <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30 text-[10px] gap-1">
                  <ShieldCheck className="h-3 w-3 inline" /> Portal Seguro SSL
                </Badge>
              </div>
              <p className="text-xs text-slate-400 mt-0.5">
                Portal de Autogestión y Pagos de Cartera &bull; {agreement.unidad_privada} ({agreement.propietario_nombre})
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 self-start sm:self-center flex-wrap">
            <Button
              variant="outline"
              size="sm"
              onClick={() => PaymentAgreementPdfService.downloadAgreementPdf(agreement)}
              className="h-7 text-xs gap-1 border-emerald-500/50 bg-emerald-950/40 text-emerald-300 hover:bg-emerald-900/60 font-semibold"
              title="Descargar Contrato de Transacción en PDF"
            >
              <Download className="h-3 w-3" /> PDF
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => PaymentAgreementPdfService.shareAgreementViaWhatsApp(agreement)}
              className="h-7 text-xs gap-1 border-emerald-500/50 text-emerald-400 hover:bg-emerald-950/60"
              title="Compartir por WhatsApp"
            >
              <Send className="h-3 w-3" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const url = agreement.portal_url || `${window.location.origin}/portal-deudor/${agreement.magic_token}`;
                window.open(url, '_blank');
              }}
              className="h-7 text-xs gap-1 bg-slate-800 text-slate-200 border-slate-700 hover:bg-slate-700"
              title="Abrir Portal Público en Pestaña Nueva"
            >
              <ExternalLink className="h-3 w-3" /> Abrir Portal
            </Button>
          </div>
        </div>

        <div className="max-h-[82vh] overflow-y-auto p-4 sm:p-6 space-y-6 bg-slate-50/60 dark:bg-slate-950/40">
          {paymentReceipt ? (
            /* Pantalla de Confirmación de Pago Exitoso */
            <div className="max-w-md mx-auto text-center space-y-5 py-6">
              <div className="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400 flex items-center justify-center mx-auto shadow-inner animate-bounce">
                <CheckCircle2 className="h-10 w-10" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-slate-900 dark:text-white">
                  ¡Transacción Exitosa!
                </h3>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  El pago ha sido registrado y aplicado a la cuenta del inmueble {agreement.unidad_privada}.
                </p>
              </div>

              <Card className="border border-emerald-200 dark:border-emerald-900/60 shadow-sm text-left bg-white dark:bg-slate-900">
                <CardHeader className="pb-2 border-b">
                  <CardTitle className="text-xs font-semibold uppercase text-muted-foreground flex items-center justify-between">
                    <span>Comprobante Oficial de Recaudo</span>
                    <Badge variant="outline" className="border-emerald-300 text-emerald-600 text-[10px]">
                      APROBADO
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2.5 pt-3 text-xs">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Referencia de Pago:</span>
                    <span className="font-mono font-bold text-slate-800 dark:text-slate-200">{paymentReceipt.referencia}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Monto Acreditado:</span>
                    <span className="font-bold text-emerald-600 text-sm">${paymentReceipt.monto.toLocaleString('es-CO')} COP</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Fecha y Hora:</span>
                    <span>{paymentReceipt.fecha}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Canal / Pasarela:</span>
                    <span className="font-medium">{paymentReceipt.metodo} ({paymentReceipt.banco})</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Inmueble Beneficiario:</span>
                    <span className="font-semibold">{agreement.unidad_privada}</span>
                  </div>
                </CardContent>
              </Card>

              <div className="flex flex-wrap gap-2 justify-center">
                {agreement.estado === 'cumplido' || (agreement.total_recaudado + paymentReceipt.monto >= agreement.deuda_pactada) ? (
                  <Button
                    size="sm"
                    onClick={() => {
                      PaymentAgreementPdfService.downloadPazYSalvoPdf(agreement);
                    }}
                    className="gap-1.5 text-xs h-9 bg-emerald-600 hover:bg-emerald-700 text-white font-bold shadow-sm"
                  >
                    <Award className="h-4 w-4" /> Descargar Certificado de Paz y Salvo (PDF)
                  </Button>
                ) : null}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    PaymentAgreementPdfService.downloadAgreementPdf(agreement);
                  }}
                  className="gap-1.5 text-xs h-9 border-emerald-500 text-emerald-700 hover:bg-emerald-50 dark:hover:bg-emerald-950 font-semibold"
                >
                  <Download className="h-3.5 w-3.5" /> Descargar Acuerdo Actualizado (PDF)
                </Button>
                <Button
                  size="sm"
                  onClick={() => setPaymentReceipt(null)}
                  className="bg-slate-800 hover:bg-slate-900 text-white text-xs h-9"
                >
                  Continuar en el Portal
                </Button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              {/* Columna Izquierda: Resumen Financiero y Cronograma de Cuotas */}
              <div className="lg:col-span-6 space-y-4">
                {/* Tarjeta de Ahorro y Deuda Pactada */}
                <Card className="border border-emerald-200/80 dark:border-emerald-900/50 bg-gradient-to-br from-emerald-50/90 via-teal-50/50 to-white dark:from-emerald-950/30 dark:via-slate-900 dark:to-slate-900 shadow-sm overflow-hidden">
                  <CardContent className="p-4 sm:p-5 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold uppercase tracking-wider text-emerald-800 dark:text-emerald-300 flex items-center gap-1.5">
                        <Sparkles className="h-3.5 w-3.5 text-emerald-600" />
                        Propuesta de Acuerdo Formal
                      </span>
                      {ahorro > 0 && (
                        <Badge className="bg-emerald-600 text-white text-[10px] font-bold">
                          Descuento del {agreement.porcentaje_condonacion_intereses}% en Intereses
                        </Badge>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-3 pt-1">
                      <div>
                        <span className="text-[11px] text-slate-500 block">Deuda Original Acumulada</span>
                        <span className="text-sm font-semibold line-through text-slate-400">
                          ${agreement.deuda_total_inicial.toLocaleString('es-CO')}
                        </span>
                      </div>
                      <div>
                        <span className="text-[11px] text-emerald-700 dark:text-emerald-400 font-medium block">
                          Total a Pagar con Descuento
                        </span>
                        <span className="text-xl font-black text-emerald-900 dark:text-emerald-100">
                          ${agreement.deuda_pactada.toLocaleString('es-CO')}
                        </span>
                      </div>
                    </div>

                    {ahorro > 0 && (
                      <div className="p-2.5 rounded-lg bg-emerald-100/70 dark:bg-emerald-900/40 border border-emerald-200 dark:border-emerald-800 text-xs text-emerald-900 dark:text-emerald-200 flex items-center justify-between">
                        <span>Ahorro obtenido por acuerdo:</span>
                        <strong className="font-bold text-emerald-800 dark:text-emerald-300">
                          ${ahorro.toLocaleString('es-CO')} COP
                        </strong>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Resumen del Plan de Pagos */}
                <Card className="border shadow-sm">
                  <CardHeader className="pb-3 border-b bg-muted/20">
                    <CardTitle className="text-xs font-bold uppercase tracking-wider flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-3.5 w-3.5 text-orange-600" />
                        <span>Estructura de Cuotas y Financiación</span>
                      </div>
                      <span className="text-[11px] text-muted-foreground font-normal">
                        {agreement.numero_cuotas} cuotas de ${agreement.valor_cuota_mensual.toLocaleString('es-CO')}
                      </span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="divide-y divide-slate-100 dark:divide-slate-800 text-xs max-h-56 overflow-y-auto">
                      {/* Cuota Inicial */}
                      <div className="p-3 flex items-center justify-between bg-amber-50/50 dark:bg-amber-950/20">
                        <div>
                          <div className="font-semibold text-amber-900 dark:text-amber-200 flex items-center gap-1.5">
                            <span>Cuota Inicial ({agreement.cuota_inicial_porcentaje}%)</span>
                            {agreement.cuota_inicial_pagada ? (
                              <Badge className="bg-emerald-600 text-white text-[9px] h-4 px-1.5">PAGADA</Badge>
                            ) : (
                              <Badge variant="outline" className="border-amber-400 text-amber-700 text-[9px] h-4 px-1.5">PENDIENTE</Badge>
                            )}
                          </div>
                          <span className="text-[10px] text-muted-foreground">
                            Límite: {agreement.cuota_inicial_fecha_limite}
                          </span>
                        </div>
                        <span className="font-bold text-sm text-slate-800 dark:text-slate-200">
                          ${agreement.cuota_inicial_monto.toLocaleString('es-CO')}
                        </span>
                      </div>

                      {/* Cuotas Mensuales Financiadas */}
                      {agreement.cuotas_detalle.map((cuota) => (
                        <div key={cuota.numero} className="p-3 flex items-center justify-between hover:bg-muted/30 transition-colors">
                          <div className="space-y-0.5">
                            <div className="font-medium flex items-center gap-1.5">
                              <span>Cuota #{cuota.numero} de {agreement.numero_cuotas}</span>
                              <Badge
                                variant="outline"
                                className={`text-[9px] h-4 px-1.5 ${
                                  cuota.estado === 'pagada'
                                    ? 'bg-emerald-50 text-emerald-700 border-emerald-300'
                                    : cuota.estado === 'vencida'
                                    ? 'bg-rose-50 text-rose-700 border-rose-300'
                                    : 'text-slate-600 border-slate-300'
                                }`}
                              >
                                {cuota.estado.toUpperCase()}
                              </Badge>
                            </div>
                            <span className="text-[10px] text-slate-500">
                              Vence: {cuota.fecha_vencimiento}
                            </span>
                          </div>
                          <div className="text-right">
                            <span className="font-bold text-slate-800 dark:text-slate-200 block">
                              ${cuota.total_cuota.toLocaleString('es-CO')}
                            </span>
                            {cuota.referencia_pago && (
                              <span className="text-[9px] text-emerald-600 font-mono">
                                Ref: {cuota.referencia_pago}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Cláusula Jurídica Resumida */}
                <div className="p-3 rounded-lg border bg-blue-50/60 dark:bg-blue-950/20 border-blue-200 dark:border-blue-900/50 text-[11px] text-blue-950 dark:text-blue-200 space-y-1">
                  <div className="flex items-center gap-1.5 font-bold text-blue-900 dark:text-blue-300">
                    <Lock className="h-3.5 w-3.5" />
                    Validez Legal del Acuerdo (Art. 2469 Código Civil y Ley 675)
                  </div>
                  <p className="leading-tight text-blue-800 dark:text-blue-300 text-[10px]">
                    El presente acuerdo presta mérito ejecutivo pleno. El pago de las cuotas suspende cualquier cobro coactivo. En caso de incumplimiento de una cuota, se activa la cláusula aceleratoria sobre el saldo total sin condonación.
                  </p>
                </div>
              </div>

              {/* Columna Derecha: Firma Digital y Pasarela de Pago O Paz y Salvo si ya cumplió */}
              <div className="lg:col-span-6 space-y-4">
                {agreement.estado === 'cumplido' || (agreement.saldo_restante !== undefined && agreement.saldo_restante <= 0) || (agreement.cuotas_pagadas >= agreement.cuotas_totales && agreement.cuotas_totales > 0) ? (
                  <Card className="border-2 border-emerald-500/80 bg-gradient-to-br from-emerald-50 via-teal-50 to-white dark:from-emerald-950/40 dark:via-slate-900 dark:to-slate-900 shadow-md">
                    <CardHeader className="pb-2 text-center">
                      <div className="mx-auto w-12 h-12 rounded-full bg-emerald-100 dark:bg-emerald-900 text-emerald-600 flex items-center justify-center mb-1 shadow-inner">
                        <Award className="h-6 w-6 text-emerald-600" />
                      </div>
                      <CardTitle className="text-base font-bold text-emerald-950 dark:text-emerald-100">
                        ¡Acuerdo 100% Cumplido!
                      </CardTitle>
                      <Badge className="mx-auto bg-emerald-600 text-white text-[11px] font-bold px-3 py-0.5">
                        A PAZ Y SALVO CON LA COPROPIEDAD
                      </Badge>
                    </CardHeader>
                    <CardContent className="space-y-4 pt-2 text-center">
                      <p className="text-xs text-slate-700 dark:text-slate-300 leading-relaxed">
                        Estimado(a) <strong>{agreement.propietario_nombre}</strong>, la administración certifica que ha cancelado satisfactoriamente el 100% de las cuotas pactadas para el inmueble <strong>{agreement.unidad_privada}</strong>.
                      </p>

                      <div className="p-3 rounded-lg bg-emerald-100/70 dark:bg-emerald-950/60 border border-emerald-300 dark:border-emerald-800 text-xs text-emerald-900 dark:text-emerald-200 space-y-1.5 font-medium text-left">
                        <div className="flex justify-between text-[11px]">
                          <span>Saldo pendiente exigible:</span>
                          <strong className="text-emerald-700 dark:text-emerald-300 font-bold">$0 COP (CERO PESOS)</strong>
                        </div>
                        <div className="flex justify-between text-[11px]">
                          <span>Total recaudado verificado:</span>
                          <strong>${agreement.deuda_pactada.toLocaleString('es-CO')} COP</strong>
                        </div>
                        <div className="flex justify-between text-[11px]">
                          <span>Radicado Paz y Salvo:</span>
                          <strong className="font-mono">PYS-{agreement.id.replace(/^AGR-/, '')}-{new Date().getFullYear()}</strong>
                        </div>
                        <div className="flex justify-between text-[11px]">
                          <span>Validez probatoria:</span>
                          <span>Ley 675 de 2001 (Arts. 29 y 51)</span>
                        </div>
                      </div>

                      <div className="space-y-2 pt-2">
                        <Button
                          onClick={() => PaymentAgreementPdfService.downloadPazYSalvoPdf(agreement)}
                          className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs h-10 shadow-md gap-2"
                        >
                          <Award className="h-4 w-4" />
                          Descargar Certificado Oficial de Paz y Salvo (PDF)
                        </Button>

                        <Button
                          variant="outline"
                          onClick={() => PaymentAgreementPdfService.downloadAgreementPdf(agreement)}
                          className="w-full border-slate-300 dark:border-slate-700 text-xs h-9 gap-1.5 text-slate-700 dark:text-slate-300"
                        >
                          <Download className="h-3.5 w-3.5" />
                          Descargar Contrato de Transacción Archivado (PDF)
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ) : (
                  <>
                {/* Paso 1: Firma Electrónica con Código de Seguridad OTP */}
                <Card className={`border shadow-sm transition-all ${isSigned ? 'bg-emerald-50/30 border-emerald-300 dark:border-emerald-900' : 'bg-white dark:bg-slate-900'}`}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-xs font-bold uppercase tracking-wider flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${isSigned ? 'bg-emerald-600 text-white' : 'bg-orange-600 text-white'}`}>
                          {isSigned ? <Check className="h-3 w-3" /> : '1'}
                        </div>
                        <span>Aceptación y Firma Digital del Acuerdo</span>
                      </div>
                      {isSigned && (
                        <Badge className="bg-emerald-600 text-white text-[10px]">
                          FIRMADO DIGITALMENTE
                        </Badge>
                      )}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3 pt-1">
                    {!isSigned ? (
                      <>
                        <p className="text-xs text-muted-foreground leading-relaxed">
                          Para aceptar las condiciones pactadas y formalizar el plan de pagos con la administración, confirme su identidad mediante código seguro OTP.
                        </p>

                        <div className="flex items-center gap-2">
                          <Input
                            placeholder="Código OTP (ej. 7842)"
                            value={otpCode}
                            onChange={(e) => setOtpCode(e.target.value)}
                            className="text-xs h-9 font-mono max-w-[170px]"
                          />
                          {!isOtpSent ? (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={handleSendOtp}
                              className="text-xs h-9 gap-1"
                            >
                              <Send className="h-3 w-3" /> Enviar Código al Celular
                            </Button>
                          ) : (
                            <Button
                              type="button"
                              size="sm"
                              onClick={handleSignAgreement}
                              className="text-xs h-9 bg-emerald-600 hover:bg-emerald-700 text-white gap-1 font-semibold"
                            >
                              <CheckCircle2 className="h-3.5 w-3.5" /> Firmar Aceptación
                            </Button>
                          )}
                        </div>
                      </>
                    ) : (
                      <div className="p-3 rounded-lg bg-emerald-100/60 dark:bg-emerald-950/40 text-emerald-900 dark:text-emerald-200 text-xs flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                          <span>Acuerdo firmado por <strong>{agreement.propietario_nombre}</strong></span>
                        </div>
                        <span className="text-[10px] font-mono text-emerald-700 dark:text-emerald-400">
                          Ley 527/1999
                        </span>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Paso 2: Pasarela de Pago En Línea */}
                <Card className={`border shadow-sm ${!isSigned ? 'opacity-60 pointer-events-none' : ''}`}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-xs font-bold uppercase tracking-wider flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-5 h-5 rounded-full bg-orange-600 text-white flex items-center justify-center text-[10px] font-bold">
                          2
                        </div>
                        <span>Pagar en Línea (Sin Fricción)</span>
                      </div>
                      <span className="text-xs font-bold text-orange-600">
                        {agreement.cuota_inicial_pagada ? 'Cuota Ordinaria' : 'Cuota Inicial'}: $
                        {(agreement.cuota_inicial_pagada ? agreement.valor_cuota_mensual : agreement.cuota_inicial_monto).toLocaleString('es-CO')}
                      </span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4 pt-1">
                    <Tabs value={paymentMethod} onValueChange={(val: any) => setPaymentMethod(val)}>
                      <TabsList className="grid grid-cols-4 w-full h-8 text-[11px]">
                        <TabsTrigger value="pse" className="text-[11px] gap-1">
                          <CreditCard className="h-3 w-3" /> PSE
                        </TabsTrigger>
                        <TabsTrigger value="nequi" className="text-[11px] gap-1">
                          <Smartphone className="h-3 w-3" /> Nequi
                        </TabsTrigger>
                        <TabsTrigger value="card" className="text-[11px] gap-1">
                          <CreditCard className="h-3 w-3" /> Tarjeta
                        </TabsTrigger>
                        <TabsTrigger value="barras" className="text-[11px] gap-1">
                          <Receipt className="h-3 w-3" /> Bancos
                        </TabsTrigger>
                      </TabsList>

                      <div className="pt-3">
                        <TabsContent value="pse" className="space-y-3 m-0">
                          <div className="space-y-1">
                            <Label className="text-xs">Seleccione su Entidad Financiera</Label>
                            <select
                              value={selectedBank}
                              onChange={(e) => setSelectedBank(e.target.value)}
                              className="w-full text-xs h-9 rounded-md border border-input bg-background px-3 py-1 shadow-sm"
                            >
                              <option value="Bancolombia">Bancolombia</option>
                              <option value="Davivienda">Davivienda</option>
                              <option value="Banco de Bogotá">Banco de Bogotá</option>
                              <option value="BBVA Colombia">BBVA Colombia</option>
                              <option value="Nequi">Nequi (A través de PSE)</option>
                              <option value="Daviplata">Daviplata</option>
                              <option value="Banco de Occidente">Banco de Occidente</option>
                              <option value="Scotiabank Colpatria">Scotiabank Colpatria</option>
                            </select>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Correo Electrónico Registrado en PSE</Label>
                            <Input
                              type="email"
                              value={payerEmail}
                              onChange={(e) => setPayerEmail(e.target.value)}
                              className="text-xs h-8"
                            />
                          </div>
                        </TabsContent>

                        <TabsContent value="nequi" className="space-y-2 m-0 text-center py-2">
                          <div className="p-3 rounded-lg border bg-white dark:bg-slate-900 inline-block shadow-sm">
                            <QrCode className="h-20 w-20 mx-auto text-slate-800 dark:text-slate-100" />
                          </div>
                          <p className="text-[11px] text-muted-foreground">
                            Escanee el código QR dinámico desde su app <strong>Nequi</strong> o autorice con su número de celular.
                          </p>
                        </TabsContent>

                        <TabsContent value="card" className="space-y-2 m-0">
                          <div className="space-y-1">
                            <Label className="text-xs">Número de Tarjeta</Label>
                            <Input placeholder="4000 1234 5678 9010" className="text-xs h-8 font-mono" />
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div className="space-y-1">
                              <Label className="text-xs">Vencimiento (MM/AA)</Label>
                              <Input placeholder="12/28" className="text-xs h-8 text-center" />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">CVV</Label>
                              <Input placeholder="123" className="text-xs h-8 text-center font-mono" />
                            </div>
                          </div>
                        </TabsContent>

                        <TabsContent value="barras" className="space-y-2 m-0 text-xs">
                          <div className="p-3 rounded-lg border bg-slate-100 dark:bg-slate-800 space-y-1">
                            <div className="font-semibold text-slate-800 dark:text-slate-200">Convenio de Recaudo Bancolombia</div>
                            <div className="font-mono text-xs">Convenio No: <strong>48912</strong></div>
                            <div className="font-mono text-xs">Referencia: <strong>{agreement.unidad_privada.replace(/\D/g, '') || '504'}</strong></div>
                            <p className="text-[10px] text-slate-500 pt-1">
                              Presente este número en cualquier cajero o corresponsal bancario del país.
                            </p>
                          </div>
                        </TabsContent>
                      </div>
                    </Tabs>

                    <Button
                      onClick={handleSimulatePayment}
                      disabled={isProcessingPayment}
                      className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs h-10 shadow-md gap-2"
                    >
                      {isProcessingPayment ? (
                        <>Procesando Transacción Segura...</>
                      ) : (
                        <>
                          <Lock className="h-3.5 w-3.5" />
                          Realizar Pago de ${ (agreement.cuota_inicial_pagada ? agreement.valor_cuota_mensual : agreement.cuota_inicial_monto).toLocaleString('es-CO') } COP
                        </>
                      )}
                    </Button>
                  </CardContent>
                </Card>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
export default DebtorPortalModal;
